import type { ApiHook } from '@frontend/api-client';
import type { SetupIntent } from '@frontend/api-types';
import type { getMutationHandler } from '@frontend/duck-tape';
import { useCallback, useEffect } from 'react';

/**
 * Dependencies required for the useSetupIntent hook.
 * Uses existing types from the codebase.
 */
export type UseSetupIntentDependencies = {
  /** Standard mutation handler from duck-tape */
  handleMutation: ReturnType<typeof getMutationHandler>;
  /** Existing API hook for creating setup intents */
  useCreateSetupIntentMutation: ApiHook<'useCreateSetupIntentMutation'>;
};

/**
 * Return type using the existing setup intent mutation hook
 */
export type UseSetupIntentReturn = {
  /** Error from creating setup intent */
  createSetupIntentError?: ReturnType<UseSetupIntentDependencies['useCreateSetupIntentMutation']>[1]['error'];
  /** Loading state for setup intent creation */
  isLoading: boolean;
  /** Retry creating a setup intent */
  retryCreateSetupIntent: () => Promise<void>;
  /** Setup intent data */
  stripeSetupIntent?: SetupIntent;
};

/**
 * Cross-platform hook for managing Stripe setup intent creation.
 * Uses dependency injection to support different platforms (web/mobile).
 *
 * This hook automatically creates a setup intent when mounted and none exists,
 * following the same pattern used across the codebase.
 *
 * @param dependencies - Platform-specific dependencies for API calls and error handling
 * @returns Object containing setup intent data, error state, and loading state
 *
 * @example
 * ```typescript
 * // Web usage
 * const setupIntent = useSetupIntent({
 *   useCreateSetupIntentMutation: useCreateSetupIntentMutation,
 *   handleMutation: handleMutation
 * });
 *
 * // Mobile usage
 * const setupIntent = useSetupIntent({
 *   useCreateSetupIntentMutation: useCreateSetupIntentMutation,
 *   handleMutation: handleMutation
 * });
 * ```
 */
export const useSetupIntent = (dependencies: UseSetupIntentDependencies): UseSetupIntentReturn => {
  const { handleMutation, useCreateSetupIntentMutation } = dependencies;

  const [createSetupIntent, { data: stripeSetupIntent, error: createSetupIntentError }] =
    useCreateSetupIntentMutation();

  const retryCreateSetupIntent = useCallback(async () => {
    await handleMutation({
      mutation: () => createSetupIntent().unwrap(),
      showSuccessToast: false,
    });
  }, [createSetupIntent, handleMutation]);

  useEffect(() => {
    if (stripeSetupIntent || createSetupIntentError) {
      return;
    }

    void retryCreateSetupIntent();
  }, [stripeSetupIntent, createSetupIntentError, retryCreateSetupIntent]);

  return {
    createSetupIntentError,
    isLoading: !stripeSetupIntent && !createSetupIntentError,
    retryCreateSetupIntent,
    stripeSetupIntent,
  };
};
